// Updated BackgroundManager.js with video support
import React, { useState, useEffect } from 'react';
import './BackgroundManager.css';

//const _host = 'chappie-demo.novosense.africa';
const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const BackgroundManager = ({ onBackgroundSelected, currentBackground, onToggleIdleDisplay, showIdleDisplay: propShowIdleDisplay }) => {
  const [backgrounds, setBackgrounds] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedBackground, setSelectedBackground] = useState(currentBackground || null);
  const [showIdleDisplay, setShowIdleDisplay] = useState(false);

  // Fetch saved backgrounds when component mounts
  useEffect(() => {
    fetchBackgrounds();
    fetchDisplaySettings();
  }, []);

  // Update selectedBackground when currentBackground prop changes
  useEffect(() => {
    if (currentBackground) {
      setSelectedBackground(currentBackground);
    }
  }, [currentBackground]);

  // Set initial selected background from backend after both backgrounds and display data are loaded
  useEffect(() => {
    if (backgrounds.length > 0 && !currentBackground) {
      // Try to get the current background from display data
      const fetchDisplaySettingsForBackground = async () => {
        try {
          const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');
          if (response.ok) {
            const data = await response.json();
            const backgroundId = data.backgroundId || data.background_id;

            if (backgroundId) {
              console.log('🎬 BackgroundManager useEffect - found background_id:', backgroundId);
              const background = backgrounds.find(bg => bg.id === backgroundId);
              if (background) {
                console.log('🎬 BackgroundManager useEffect - setting selectedBackground:', background);
                setSelectedBackground(background);
              }
            }
          }
        } catch (err) {
          console.error('Error fetching display settings for background:', err);
        }
      };

      fetchDisplaySettingsForBackground();
    }
  }, [backgrounds, currentBackground]);

  // Fetch all saved backgrounds
  const fetchBackgrounds = async () => {
    try {
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/backgrounds');
      
      if (!response.ok) {
        throw new Error('Failed to fetch backgrounds');
      }
      
      const data = await response.json();
      setBackgrounds(data);
    } catch (err) {
      console.error('Error fetching backgrounds:', err);
      setError('Failed to load backgrounds. Please try again later.');
    }
  };

  // Fetch current display settings
  const fetchDisplaySettings = async () => {
    try {
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/display');

      if (response.ok) {
        const data = await response.json();
        console.log('🎬 BackgroundManager fetchDisplaySettings - received data:', data);

        // Check for both field name variants
        const showIdleDisplayValue = data.showIdleDisplay !== undefined ? data.showIdleDisplay : data.show_idle_display;

        if (showIdleDisplayValue !== undefined) {
          console.log('🎬 BackgroundManager fetchDisplaySettings - setting showIdleDisplay to:', showIdleDisplayValue);
          setShowIdleDisplay(showIdleDisplayValue);
        } else {
          console.log('🎬 BackgroundManager fetchDisplaySettings - showIdleDisplay not found in data');
        }

        // Fetch current background selection
        const backgroundId = data.backgroundId || data.background_id;
        if (backgroundId) {
          console.log('🎬 BackgroundManager - found background_id in display data:', backgroundId);
          // Only set selectedBackground if it differs from current prop or prop is null
          if (!currentBackground || (currentBackground && currentBackground.id !== backgroundId)) {
            // Find the background details from the backgrounds array
            if (backgrounds.length > 0) {
              const background = backgrounds.find(bg => bg.id === backgroundId);
              if (background) {
                console.log('🎬 BackgroundManager - setting selectedBackground:', background);
                setSelectedBackground(background);
              } else {
                console.log('🎬 BackgroundManager - background not found in backgrounds array');
              }
            }
          }
        } else {
          console.log('🎬 BackgroundManager - no background_id found in display data');
          // Only clear selection if there's no prop and we currently have something selected
          if (!currentBackground && selectedBackground !== null) {
            console.log('🎬 BackgroundManager - clearing selectedBackground to match no background');
            setSelectedBackground(null);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching display settings:', err);
    }
  };

  // Handle file upload
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    const validTypes = [...validImageTypes, ...validVideoTypes];
    
    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a JPG, PNG, GIF image or MP4, WebM, OGG video.');
      return;
    }
    
    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      setError('File too large. Please upload a file smaller than 15MB.');
      return;
    }
    
    // Create FormData object
    const formData = new FormData();
    formData.append('background', file);
    formData.append('type', file.type.startsWith('video/') ? 'video' : 'image');
    
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    
    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 300);
      
      // Upload file
      const response = await fetch('https://'+_host+':6003/api/'+_company+'/backgrounds', {
        method: 'POST',
        body: formData
      });
      
      clearInterval(progressInterval);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload background');
      }
      
      setUploadProgress(100);
      
      // Get the new background data
      const backgroundData = await response.json();
      
      // Add the new background to the list
      setBackgrounds(prev => [...prev, backgroundData]);
      
      // Show success message
      setSuccess('Background uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh backgrounds list
      fetchBackgrounds();
    } catch (err) {
      console.error('Error uploading background:', err);
      setError(err.message || 'Failed to upload background');
    } finally {
      setIsUploading(false);
      // Reset file input
      e.target.value = '';
    }
  };

  // Handle background selection
  const handleSelectBackground = (background) => {
    setSelectedBackground(background);
    
    // Call parent component callback
    if (onBackgroundSelected) {
      onBackgroundSelected(background);
    }
  };

  // Handle toggle for idle display - delegate to parent component
  const handleToggleIdleDisplay = () => {
    const newValue = !showIdleDisplay;
    setShowIdleDisplay(newValue);

    // Delegate to parent component's toggle function
    if (onToggleIdleDisplay) {
      console.log('BackgroundManager calling parent toggle with value:', newValue);
      onToggleIdleDisplay(newValue);
    }
  };

  // Handle background deletion
  const handleDeleteBackground = async (backgroundId) => {
    try {
      const confirmDelete = window.confirm(
        'Are you sure you want to delete this background?'
      );
      
      if (!confirmDelete) return;
      
      const response = await fetch(`https://${_host}:6003/api/${_company}/backgrounds/${backgroundId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete background');
      }
      
      // Remove the deleted background from the list
      setBackgrounds(prev => prev.filter(bg => bg.id !== backgroundId));
      
      // If the deleted background was selected, clear selection
      if (selectedBackground && selectedBackground.id === backgroundId) {
        setSelectedBackground(null);
        if (onBackgroundSelected) {
          onBackgroundSelected(null);
        }
      }
      
      // Show success message
      setSuccess('Background deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting background:', err);
      setError(err.message || 'Failed to delete background');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Function to render the appropriate preview based on media type
  const renderBackgroundPreview = (background) => {
    // Fix background URL issues (duplicate protocols, missing colons, etc.)
    let fixedUrl = background.url;
    if (background.url) {
      // If URL already has a protocol, use it as-is
      if (background.url.startsWith('http://') || background.url.startsWith('https://')) {
        fixedUrl = background.url;
      }
      // Fix malformed URLs with missing colons
      else if (background.url.includes('https//')) {
        fixedUrl = background.url.replace('https//', 'https://');
      } else if (background.url.includes('http//')) {
        fixedUrl = background.url.replace('http//', 'http://');
      }
      // If it's a relative path, prepend with host
      else if (!background.url.startsWith('http')) {
        fixedUrl = `https://${_host}${background.url}`;
      }
    }

    if (background.type === 'video') {
      return (
        <video
          src={fixedUrl}
          alt={background.name || 'Video background'}
          onClick={() => handleSelectBackground(background)}
          muted
          autoPlay={false}
          loop
          className="preview-video"
        />
      );
    } else {
      return (
        <img
          src={fixedUrl}
          alt={background.name || 'Background'}
          onClick={() => handleSelectBackground(background)}
        />
      );
    }
  };

  return (
    <div className="background-manager">
      <h3>Background Media</h3>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="upload-section">
        <label htmlFor="background-upload" className="upload-label">
          Upload New Background (Image or Video)
        </label>
        <input
          type="file"
          id="background-upload"
          accept="image/jpeg,image/png,image/gif,video/mp4,video/webm,video/ogg"
          onChange={handleFileChange}
          disabled={isUploading}
          className="file-input"
        />
        
        {isUploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
            <span>{uploadProgress}%</span>
          </div>
        )}
      </div>
      
      <div className="display-settings">
        <h4>Display Settings</h4>
        <div className="toggle-container">
          <label className="toggle-label">
            Show background ONLY (No Heats displayed)
            <div className="toggle-switch">
              <input
                type="checkbox"
                checked={propShowIdleDisplay}
                onChange={handleToggleIdleDisplay}
                className="toggle-input"
              />
              <span className="toggle-slider"></span>
            </div>
          </label>
        </div>
      </div>
      
      <div className="backgrounds-gallery">
        <div 
          className={`background-item ${selectedBackground === null ? 'selected' : ''}`}
          onClick={() => handleSelectBackground(null)}
        >
          <div className="background-preview default">
            <span>Default</span>
          </div>
          <div className="background-name">Default Background</div>
        </div>
        
        {backgrounds.map(background => (
          <div 
            key={background.id} 
            className={`background-item ${selectedBackground && selectedBackground.id === background.id ? 'selected' : ''}`}
          >
            <div className="background-preview">
              {renderBackgroundPreview(background)}
              <button 
                className="delete-background-btn"
                onClick={() => handleDeleteBackground(background.id)}
                title="Delete background"
              >
                ×
              </button>
            </div>
            <div className="background-name">
              {background.name || `Background ${background.id}`}
              {background.type === 'video' && <span className="type-badge">Video</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BackgroundManager;